/**
 * Leadership / administration catalogue. Slug used at /about/leadership/:slug.
 */

export interface LeadershipMember {
  slug: string;
  name: string;
  role: string;
  /** Short tag — e.g. "Principal · since 2019". */
  term: string;
  bio: string[];
  vision: string;
  /** Notable career milestones. */
  career: Array<{ year: string; what: string }>;
  /** Areas of focus / portfolio in the current role. */
  portfolio: string[];
  contact: { email: string; phone?: string; office?: string };
}

export const LEADERS: LeadershipMember[] = [
  {
    slug: "shamanur-shivashankarappa",
    name: "Dr. Shamanur Shivashankarappa",
    role: "Secretary, Bapuji Educational Association",
    term: "Secretary · since 1972",
    bio: [
      "Dr. Shamanur Shivashankarappa is a five-time MLA from Davanagere South and the Secretary of Bapuji Educational Association (BEA) — the trust that runs over 50 institutions including BIET. As Secretary, he has overseen BEA's expansion from a single school in 1958 to its present footprint across central Karnataka.",
      "Under his stewardship, BIET earned its autonomous status in 2024, the milestone 50-institution mark for the BEA group, and the consistent NAAC and NBA recognition that the institute holds today.",
    ],
    vision:
      "Engineering education that takes graduates further than a degree — by anchoring students in the workshops, the labs, and the problems of the region we serve.",
    career: [
      { year: "1972", what: "Becomes Secretary of Bapuji Educational Association" },
      { year: "1979", what: "Founds Bapuji Institute of Engineering & Technology" },
      { year: "1985", what: "First MLA term · Davanagere South" },
      { year: "2024", what: "BIET conferred autonomous status under his chairmanship" },
    ],
    portfolio: ["Trust governance", "Institutional strategy", "Academic council oversight"],
    contact: { email: "secretary@bea-edu.org" },
  },
  {
    slug: "ss-mallikarjun",
    name: "Sri. SS Mallikarjun",
    role: "Joint Secretary, BEA",
    term: "Joint Secretary",
    bio: [
      "Sri. SS Mallikarjun serves as Joint Secretary of the Bapuji Educational Association, working closely with the Secretary on academic strategy, infrastructure investment, and partnerships. He has been instrumental in BEA's recent capital programme — the new Innovation Hub, the BH-B residential block, and the renovation of the central library.",
    ],
    vision:
      "Build the spaces — labs, workshops, residences — where the muscle memory of an engineer is formed.",
    career: [
      { year: "Recent", what: "Led the BEA capital programme · Innovation Hub + BH-B + library renovation" },
    ],
    portfolio: ["Infrastructure", "Capital strategy", "Industry partnerships"],
    contact: { email: "joint-secretary@bea-edu.org" },
  },
  {
    slug: "prabha-mallikarjun",
    name: "Dr. Prabha Mallikarjun",
    role: "Member, Governing Body",
    term: "Governing Body member",
    bio: [
      "Dr. Prabha Mallikarjun serves on the Governing Body of BIET, with responsibility for academic policy and women-in-engineering initiatives. She has been a vocal champion of the WIE adoption scholarship programme and the BIET Women's Cell.",
    ],
    vision:
      "Make engineering as accessible to a girl in rural Karnataka as it is to a student in Bengaluru.",
    career: [
      { year: "2018", what: "Initiated the WIE adoption scholarship programme at BIET" },
    ],
    portfolio: ["Academic policy", "Women in engineering", "Scholarships"],
    contact: { email: "governing-body@bietdvg.edu" },
  },
  {
    slug: "hb-aravind",
    name: "Dr. H.B. Aravind",
    role: "Principal, BIET",
    term: "Principal · since 2019",
    bio: [
      "Dr. H.B. Aravind has been Principal of BIET since 2019. Under his stewardship the institute earned its autonomous status (2024), launched fourteen new programme specialisations, doubled active research grants, and crossed the four-hundred-offer mark in annual placements for the first time.",
      "A Mechanical Engineer by training and a long-serving BIET faculty member before his appointment as Principal, Dr. Aravind is known for the open-door policy he has maintained throughout his tenure — Wednesday office hours are open to any student, faculty member or staff with a half-page note in advance.",
    ],
    vision:
      "Forty-six years of building engineers — and we are only beginning. The next ten years are about turning the muscle memory of this institute into a research-active, autonomous, internationally-engaged engineering school.",
    career: [
      { year: "1992", what: "Joins BIET as Assistant Professor · Mechanical Engineering" },
      { year: "2008", what: "Promoted to Professor; HoD · Mechanical" },
      { year: "2014", what: "Dean (Academics)" },
      { year: "2019", what: "Appointed Principal" },
      { year: "2024", what: "Leads BIET's autonomous-status conferral" },
    ],
    portfolio: ["Academic governance", "Faculty development", "Strategic partnerships", "Autonomous-curriculum oversight"],
    contact: { email: "principal@bietdvg.edu", phone: "+91 8192 233 802", office: "Main Block · Room 101" },
  },
  {
    slug: "r-nagaraj",
    name: "Name Placeholder",
    role: "Dean (Academics)",
    term: "Dean (Academics) · since 2022",
    bio: [
      "Name Placeholder has served as Dean (Academics) since 2022. The Dean's office anchors curriculum design, academic calendar planning, and the BIET Board of Studies — all of which acquired new operational weight after the 2024 autonomous-status conferral.",
    ],
    vision:
      "An autonomous curriculum is a privilege. The dean's job is to make sure that privilege is exercised with rigor and humility — every revision must justify itself.",
    career: [
      { year: "2002", what: "Joins BIET as Assistant Professor" },
      { year: "2018", what: "HoD · CSE" },
      { year: "2022", what: "Appointed Dean (Academics)" },
    ],
    portfolio: ["Curriculum design", "Academic calendar", "Board of Studies", "Examination reform"],
    contact: { email: "dean.academics@bietdvg.edu", phone: "+91 8192 233 803", office: "Main Block · Room 102" },
  },
  {
    slug: "k-sundaresh",
    name: "Name Placeholder",
    role: "Dean (Research)",
    term: "Dean (Research) · since 2021",
    bio: [
      "The Dean (Research) office oversees the institute's fifteen VTU-recognised research centres, which together support 117 active Ph.D. scholars across nine departments, hold ₹1.49 crore in grants from VGST, K-FIST, AICTE, NAIN, VTU, GOI and KSCST, and have produced 348 indexed publications over the past five years.",
    ],
    vision:
      "Research at BIET should solve problems we can see from the campus gate — water, energy, agriculture, manufacturing, healthcare. Global recognition follows local relevance, not the other way around.",
    career: [
      { year: "1998", what: "Joins BIET as Lecturer · ECE" },
      { year: "2014", what: "Promoted to Professor" },
      { year: "2021", what: "Appointed Dean (Research)" },
    ],
    portfolio: ["Research strategy", "Grant administration", "Doctoral supervision policy", "Industry-sponsored programmes"],
    contact: { email: "dean.research@bietdvg.edu", phone: "+91 8192 233 804", office: "Research Block · Room R-101" },
  },
  {
    slug: "m-lakshmi",
    name: "Name Placeholder",
    role: "Dean (Student Affairs)",
    term: "Dean (Student Affairs) · since 2020",
    bio: [
      "Name Placeholder has served as Dean (Student Affairs) since 2020. The Dean's office covers clubs and forums, student welfare, hostels liaison, scholarships, and the campus's mental-health services.",
    ],
    vision:
      "Every student should leave BIET with at least one thing besides a degree — a club they led, a paper they wrote, a friend they made who challenged them. That is what student affairs exists to make easy.",
    career: [
      { year: "2005", what: "Joins BIET as Assistant Professor · MBA" },
      { year: "2015", what: "Coordinator · BIET Innovation Council" },
      { year: "2020", what: "Appointed Dean (Student Affairs)" },
    ],
    portfolio: ["Clubs and forums", "Scholarships", "Hostels liaison", "Mental-health services", "Anti-ragging coordination"],
    contact: { email: "dean.student-affairs@bietdvg.edu", phone: "+91 8192 233 805", office: "Block A · Room 14" },
  },
  {
    slug: "p-krishnamurthy",
    name: "Name Placeholder",
    role: "Registrar",
    term: "Registrar · since 2017",
    bio: [
      "Name Placeholder is the Registrar of BIET, responsible for institutional records, statutory filings, transcript issuance, and the operational administration of the institute. The Registrar's office sits at the centre of the institute's compliance work — UGC, AICTE, NBA, NAAC, VTU and the Government of Karnataka.",
    ],
    vision:
      "The Registrar's office is the institute's memory. We exist so that any record — a 1985 transcript, a 2024 NAAC submission, a Friday tea-room minute — can be found when needed.",
    career: [
      { year: "1995", what: "Joins BEA as Administrative Officer" },
      { year: "2017", what: "Appointed Registrar of BIET" },
    ],
    portfolio: ["Statutory filings", "Records and transcripts", "Compliance liaison", "Office of the registrar administration"],
    contact: { email: "registrar@bietdvg.edu", phone: "+91 8192 233 806", office: "Main Block · Registrar's office" },
  },
];

export function getLeader(slug: string): LeadershipMember | undefined {
  return LEADERS.find((l) => l.slug === slug);
}
